
import Vue from "vue";
import { constants } from "@/utils/constants";
import {
  getCurrentAdminRole,
  getSncMerchRecipients,
  markSncMerchRecipientDone,
  undoSncMerchRecipientDone,
  SncMerchRecipientRow
} from "@/api";

export default Vue.extend({
  name: "SncMerchRecipients",
  data() {
    return {
      tab: 0,
      loading: false,
      actionId: null as number | null,
      isDeveloper: false,
      error: "",
      success: "",
      activeRows: [] as SncMerchRecipientRow[],
      historyRows: [] as SncMerchRecipientRow[],
      activeHeaders: [
        { text: "Talent", value: "talentName" },
        { text: "Hyg ID", value: "hygienistId" },
        { text: "Email", value: "talentEmail" },
        { text: "Tier", value: "talentTier" },
        { text: "Shifts", value: "completedCount" },
        { text: "Milestone", value: "milestoneLabel" },
        { text: "Mailing Address", value: "mailingAddress", width: "28%" },
        { text: "Earned", value: "earnedOn" },
        { text: "", value: "actions", sortable: false, align: "end" }
      ],
      historyHeaders: [
        { text: "Talent", value: "talent" },
        { text: "Milestone", value: "milestoneLabel" },
        { text: "Completed", value: "completed" },
        { text: "", value: "actions", sortable: false, align: "end" }
      ]
    };
  },
  computed: {
    activeCount(): number {
      return this.activeRows.length;
    },
    historyCount(): number {
      return this.historyRows.length;
    }
  },
  async created() {
    await this.resolveAccess();
    if (this.isDeveloper) {
      await this.loadAll();
    }
  },
  methods: {
    async resolveAccess() {
      try {
        const me = await getCurrentAdminRole();
        this.isDeveloper = !!me.isDeveloper;
      } catch {
        const userStr = localStorage.getItem("user");
        if (!userStr) {
          this.isDeveloper = false;
          return;
        }
        try {
          const user = JSON.parse(userStr);
          const role =
            user && user.role !== undefined
              ? parseInt(String(user.role), 10)
              : 0;
          this.isDeveloper = role === constants.ADMIN_ROLES.DEVELOPER;
        } catch {
          this.isDeveloper = false;
        }
      }
    },
    async loadAll() {
      this.loading = true;
      this.error = "";
      try {
        const [active, history] = await Promise.all([
          getSncMerchRecipients("active"),
          getSncMerchRecipients("history")
        ]);
        this.activeRows = active;
        this.historyRows = history;
      } catch (e) {
        this.error = e?.message || "Failed to load merch recipients";
      } finally {
        this.loading = false;
      }
    },
    milestoneColor(milestone: number): string {
      if (milestone === 3) return "#C9A227";
      if (milestone === 5) return "#13CA82";
      if (milestone === 10) return "#0B4598";
      return "#607D8B";
    },
    formatDate(value: string | null): string {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric"
      });
    },
    formatDateTime(value: string | null): string {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit"
      });
    },
    async markDone(item: SncMerchRecipientRow) {
      this.actionId = item.id;
      this.error = "";
      this.success = "";
      try {
        await markSncMerchRecipientDone(item.id);
        this.success = `Marked done for ${item.talentName}`;
        await this.loadAll();
        this.tab = 1;
      } catch (e) {
        this.error = e?.message || "Failed to mark done";
      } finally {
        this.actionId = null;
      }
    },
    async undoDone(item: SncMerchRecipientRow) {
      this.actionId = item.id;
      this.error = "";
      this.success = "";
      try {
        await undoSncMerchRecipientDone(item.id);
        this.success = `Returned ${item.talentName} to Active`;
        await this.loadAll();
        this.tab = 0;
      } catch (e) {
        this.error = e?.message || "Failed to undo";
      } finally {
        this.actionId = null;
      }
    }
  }
});
